import { AsyncStatus } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { AwayStatus } from 'apis/presence';
import { AppLink } from 'components/appLink';
import { sortPresence } from 'components/menu/utils';
import { PresenceListSection } from 'components/presence/components/presenceListSection';
import { PresenceSectionSkeleton } from 'components/presence/components/presenceSectionSkeleton';
import { PresenceSectionWarning } from 'components/presence/components/presenceSectionWarning';
import type { PresenceStateT } from 'components/presence/reducers/presence';

import styles from './favoritesPresenceListSection.module.scss';

type FavoritesPresenceListSectionProps = {
  presence: PresenceStateT;
  presenceAsyncStatus: AsyncStatus;
  profile: User;
  awayStatuses: AwayStatus[];
  favouriteTeamMemberIds: string[];
};

export const FavoritesPresenceListSection = ({
  presence,
  presenceAsyncStatus,
  profile,
  awayStatuses,
  favouriteTeamMemberIds,
}: FavoritesPresenceListSectionProps): JSX.Element => {
  const { allPresence } = presence;
  const sortedFavoritePresence = useMemo(() => {
    const favoriteMap = new Set<string>(favouriteTeamMemberIds);
    const favoritePresence = allPresence.filter((userPresence) =>
      favoriteMap.has(userPresence.user.id),
    );
    return sortPresence(favoritePresence);
  }, [favouriteTeamMemberIds, allPresence]);

  const isEmpty = sortedFavoritePresence.length === 0;
  const isLoading = presenceAsyncStatus === 'loading';

  if (isLoading) {
    return <PresenceSectionSkeleton />;
  }

  if (isEmpty) {
    return (
      <PresenceSectionWarning
        title="Add favorites to see them here"
        message={
          <>
            Add team members to your favorites by clicking the star icon on the{' '}
            <AppLink path="/team" className={styles.teamOverviewLink}>
              Team overview
            </AppLink>
          </>
        }
      />
    );
  }

  return (
    <PresenceListSection
      key="favorites"
      title={`Favorites (${sortedFavoritePresence.length})`}
      presenceList={sortedFavoritePresence}
      profile={profile}
      ownPresence={presence.presence}
      awayStatuses={awayStatuses}
    />
  );
};
